import { useState, useRef, useEffect } from 'react';
import Footer from '../home/components/Footer';
import Navigation from '../home/components/Navigation';
import { supabase } from '../../lib/supabase';

export default function ArtistsPage() {
  const [formData, setFormData] = useState({
    artistName: '',
    email: '',
    cityState: '',
    musicLink: '',
    bio: '',
    featuredSong: '',
    songTitle: '',
    albumName: '',
    releaseDate: '',
    genre: '',
    artworkDescription: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // MP3 file states
  const [mp3File, setMp3File] = useState<File | null>(null);
  const [mp3Error, setMp3Error] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Voice memo states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const handleMp3Select = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setMp3Error(null);
    
    if (file) {
      if (!file.type.includes('audio/mpeg') && !file.name.toLowerCase().endsWith('.mp3')) {
        setMp3Error('Please select a valid MP3 file.');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        setMp3Error('File size must be under 15MB.');
        return;
      }
      setMp3File(file);
    }
  };

  const removeMp3 = () => {
    setMp3File(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const updateAudioLevel = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    const normalizedLevel = Math.min(100, (average / 255) * 100);
    
    setAudioLevel(normalizedLevel);
    
    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const startRecording = async () => {
    try {
      setRecordingError(null);
      
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setRecordingError('Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Safari.');
        return;
      }

      // Request microphone access with mobile-friendly constraints
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        setRecordingError('Audio recording is not supported in your browser. Please use a modern browser.');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      // Check for supported MIME types (mobile Safari compatibility)
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }

      // Set up audio analysis for waveform (with error handling for mobile)
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          const audioContext = new AudioContextClass();
          audioContextRef.current = audioContext;
          
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyserRef.current = analyser;
          
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
          
          // Start audio level monitoring
          updateAudioLevel();
        }
      } catch (audioError) {
        console.warn('Audio visualization not available:', audioError);
        // Continue without visualization
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        setAudioLevel(0);
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setRecordingError('An error occurred while recording. Please try again.');
        stopRecording();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start audio level monitoring
      updateAudioLevel();
      
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Recording error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setRecordingError('Microphone access denied. Please allow microphone access in your browser settings and try again.');
        } else if (err.name === 'NotFoundError') {
          setRecordingError('No microphone found. Please connect a microphone and try again.');
        } else if (err.name === 'NotReadableError') {
          setRecordingError('Microphone is already in use by another application. Please close other apps and try again.');
        } else if (err.name === 'OverconstrainedError') {
          setRecordingError('Unable to access microphone with the requested settings. Please try again.');
        } else {
          setRecordingError('Unable to access microphone. Please check your browser settings and try again.');
        }
      } else {
        setRecordingError('Unable to access microphone. Please check your browser settings and try again.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (err) {
        console.error('Error stopping recording:', err);
      }
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setRecordingError(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Validate required fields
      if (!formData.artistName.trim() || !formData.email.trim() || !formData.cityState.trim() || 
          !formData.musicLink.trim() || !formData.featuredSong.trim() || !formData.bio.trim()) {
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Generate unique ID for file names
      const uniqueId = Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9);
      const artistSlug = formData.artistName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

      let voiceMemoUrl = null;
      let mp3FileUrl = null;

      // Upload voice memo if exists
      if (audioBlob) {
        const voiceMemoFileName = `voice-memos/${artistSlug}-${uniqueId}.webm`;
        const { data: voiceData, error: voiceError } = await supabase.storage
          .from('audio-files')
          .upload(voiceMemoFileName, audioBlob, {
            contentType: 'audio/webm',
            cacheControl: '3600'
          });

        if (voiceError) {
          console.error('Voice memo upload error:', voiceError);
        } else {
          const { data: voiceUrlData } = supabase.storage
            .from('audio-files')
            .getPublicUrl(voiceMemoFileName);
          voiceMemoUrl = voiceUrlData.publicUrl;
        }
      }

      // Upload MP3 file if exists
      if (mp3File) {
        const mp3FileName = `mp3-files/${artistSlug}-${uniqueId}.mp3`;
        const { data: mp3Data, error: mp3Error } = await supabase.storage
          .from('audio-files')
          .upload(mp3FileName, mp3File, {
            contentType: 'audio/mpeg',
            cacheControl: '3600'
          });

        if (mp3Error) {
          console.error('MP3 upload error:', mp3Error);
        } else {
          const { data: mp3UrlData } = supabase.storage
            .from('audio-files')
            .getPublicUrl(mp3FileName);
          mp3FileUrl = mp3UrlData.publicUrl;
        }
      }

      // Prepare data for Supabase
      const submissionData = {
        artist_name: formData.artistName.trim(),
        email: formData.email.trim(),
        city_state: formData.cityState.trim(),
        music_link: formData.musicLink.trim(),
        featured_song: formData.featuredSong.trim(),
        bio: formData.bio.trim(),
        song_title: formData.songTitle.trim() || null,
        album_name: formData.albumName.trim() || null,
        release_date: formData.releaseDate || null,
        genre: formData.genre || null,
        artwork_description: formData.artworkDescription.trim() || null,
        has_mp3_file: !!mp3File,
        has_voice_memo: !!audioBlob,
        voice_memo_url: voiceMemoUrl,
        mp3_file_url: mp3FileUrl
      };

      // Insert into Supabase
      const { error } = await supabase
        .from('artist_submissions')
        .insert([submissionData]);

      if (error) {
        console.error('Supabase error:', error);
        setSubmitStatus('error');
        return;
      }

      // Send email notification
      try {
        await fetch('https://wpncujqtmqgdeqhqmncm.supabase.co/functions/v1/notify-new-submission', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...submissionData,
            created_at: new Date().toISOString()
          }),
        });
      } catch (notifyError) {
        console.error('Notification error:', notifyError);
        // Don't fail the submission if notification fails
      }

      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        artistName: '',
        email: '',
        cityState: '',
        musicLink: '',
        bio: '',
        featuredSong: '',
        songTitle: '',
        albumName: '',
        releaseDate: '',
        genre: '',
        artworkDescription: ''
      });
      deleteRecording();
      removeMp3();
      
      // Scroll to success message
      setTimeout(() => {
        const successMessage = document.querySelector('.bg-green-50');
        if (successMessage) {
          successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-amber-900 via-orange-800 to-red-900">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/6972eef1b1e5f9a34f6274822af5f766.jpg"
            alt="For Artists"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight mb-6 font-display">
            For Artists
          </h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-orange-300 font-bold mb-10 leading-relaxed max-w-3xl mx-auto">
            Get heard by real people—rooted listeners who show up.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#submit"
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-600/50 whitespace-nowrap cursor-pointer"
            >
              Submit Music
            </a>
            <a 
              href="#how-we-work"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all duration-300 border-2 border-white/30 hover:border-white/50 whitespace-nowrap cursor-pointer"
            >
              Book a Feature
            </a>
          </div>
        </div>
      </section>

      {/* Artist Story Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-12 text-center font-display">
            The Artists Carrying Country Forward
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The artists we play aren't chasing trends. They're writing from memory, from place, from something that happened on a back road or in a bar parking lot at 2 a.m. They know the difference between a song that sounds good and a song that stays with you.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              This is music rooted in honesty. It doesn't need to explain itself. It doesn't apologize for where it comes from or who it's for. It just is—raw, real, and built to last longer than a algorithm cycle.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              We're looking for artists who understand that craft matters. Who know that a great song isn't about production tricks or viral moments—it's about truth, timing, and the kind of storytelling that makes people feel something they didn't know they needed to feel.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              If your music comes from a real place, if it's built on tradition but not stuck in it, if it sounds like something people would actually turn up in their truck—we want to hear it.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed font-bold">
              This is for the artists who are in it for the long haul. The ones who care more about the song than the stream count. The ones who know that real listeners don't just click—they commit.
            </p>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-16 text-center font-display">
            How We Work
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* What We Look For */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-search-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900 uppercase mb-4">
                What We Look For
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Real songwriting</strong> — stories that matter, not formulas</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Rooted sound</strong> — tradition-aware, not trend-chasing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Listener-first</strong> — music made for people, not playlists</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Craft over hype</strong> — substance that lasts</span>
                </li>
              </ul>
            </div>

            {/* What Artists Get */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-gift-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900 uppercase mb-4">
                What Artists Get
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Curated airplay</strong> — real rotation, not one-offs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Featured spots</strong> — artist highlights and deep dives</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Interviews</strong> — tell your story, your way</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Engaged audience</strong> — listeners who actually care</span>
                </li>
              </ul>
            </div>

            {/* What To Expect */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900 uppercase mb-4">
                What To Expect
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Review window</strong> — we listen to everything submitted</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Curation process</strong> — quality over quantity, always</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>No pay-to-play</strong> — merit-based selection only</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-orange-600 text-xl mt-0.5"></i>
                  <span><strong>Honest feedback</strong> — we respect your work</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section id="submit" className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-4 font-display">
              Submit Your Music
            </h2>
            <p className="text-gray-600 text-lg">
              Ready to reach listeners who care? Fill out the form below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 sm:p-10 shadow-lg border border-gray-200" data-readdy-form id="artist-submission-form">
            <div className="space-y-6">
              <div>
                <label htmlFor="artistName" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Artist Name <span className="text-orange-600">*</span>
                </label>
                <input
                  type="text"
                  id="artistName"
                  name="artistName"
                  required
                  value={formData.artistName}
                  onChange={(e) => setFormData({...formData, artistName: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Your artist or band name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Email <span className="text-orange-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="cityState" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  City/State <span className="text-orange-600">*</span>
                </label>
                <input
                  type="text"
                  id="cityState"
                  name="cityState"
                  required
                  value={formData.cityState}
                  onChange={(e) => setFormData({...formData, cityState: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Austin, TX"
                />
              </div>

              <div>
                <label htmlFor="musicLink" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Music Link <span className="text-orange-600">*</span>
                </label>
                <input
                  type="url"
                  id="musicLink"
                  name="musicLink"
                  required
                  value={formData.musicLink}
                  onChange={(e) => setFormData({...formData, musicLink: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Spotify, SoundCloud, or website link"
                />
              </div>

              <div>
                <label htmlFor="featuredSong" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Featured Song <span className="text-orange-600">*</span>
                </label>
                <input
                  type="text"
                  id="featuredSong"
                  name="featuredSong"
                  required
                  value={formData.featuredSong}
                  onChange={(e) => setFormData({...formData, featuredSong: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Your best track for us to review"
                />
              </div>

              {/* MP3 Upload Section */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                  Upload MP3 File
                </label>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".mp3,audio/mpeg"
                    onChange={handleMp3Select}
                    className="hidden"
                    id="mp3Upload"
                    name="mp3Upload"
                  />
                  
                  {mp3Error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        {mp3Error}
                      </p>
                    </div>
                  )}

                  {!mp3File ? (
                    <div className="text-center">
                      <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                        <i className="ri-music-2-line text-orange-600 text-3xl"></i>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">Upload your track for review (MP3 format, max 15MB)</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-upload-2-line mr-2"></i>
                        Choose MP3 File
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-music-2-fill text-green-600 text-2xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 text-sm truncate">{mp3File.name}</p>
                          <p className="text-gray-500 text-xs">{formatFileSize(mp3File.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={removeMp3}
                          className="w-10 h-10 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-xl"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Song Metadata Section */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
                  Song Metadata
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="songTitle" className="block text-sm font-bold text-gray-700 mb-2">
                      Song Title
                    </label>
                    <input
                      type="text"
                      id="songTitle"
                      name="songTitle"
                      value={formData.songTitle}
                      onChange={(e) => setFormData({...formData, songTitle: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                      placeholder="Track title"
                    />
                  </div>

                  <div>
                    <label htmlFor="albumName" className="block text-sm font-bold text-gray-700 mb-2">
                      Album / EP Name
                    </label>
                    <input
                      type="text"
                      id="albumName"
                      name="albumName"
                      value={formData.albumName}
                      onChange={(e) => setFormData({...formData, albumName: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                      placeholder="Album or EP name"
                    />
                  </div>

                  <div>
                    <label htmlFor="releaseDate" className="block text-sm font-bold text-gray-700 mb-2">
                      Release Date
                    </label>
                    <input
                      type="date"
                      id="releaseDate"
                      name="releaseDate"
                      value={formData.releaseDate}
                      onChange={(e) => setFormData({...formData, releaseDate: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                    />
                  </div>

                  <div>
                    <label htmlFor="genre" className="block text-sm font-bold text-gray-700 mb-2">
                      Genre / Style
                    </label>
                    <select
                      id="genre"
                      name="genre"
                      value={formData.genre}
                      onChange={(e) => setFormData({...formData, genre: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900 cursor-pointer"
                    >
                      <option value="">Select genre</option>
                      <option value="Traditional Country">Traditional Country</option>
                      <option value="Outlaw Country">Outlaw Country</option>
                      <option value="Americana">Americana</option>
                      <option value="Texas Country">Texas Country</option>
                      <option value="Red Dirt">Red Dirt</option>
                      <option value="Honky Tonk">Honky Tonk</option>
                      <option value="Bluegrass">Bluegrass</option>
                      <option value="Country Folk">Country Folk</option>
                      <option value="Alt-Country">Alt-Country</option>
                      <option value="Western Swing">Western Swing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Artwork Info Section */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
                  Artwork Information
                </h3>
                
                <div>
                  <label htmlFor="artworkDescription" className="block text-sm font-bold text-gray-700 mb-2">
                    Artwork Description / Link
                  </label>
                  <textarea
                    id="artworkDescription"
                    name="artworkDescription"
                    rows={3}
                    maxLength={500}
                    value={formData.artworkDescription}
                    onChange={(e) => setFormData({...formData, artworkDescription: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900 resize-none"
                    placeholder="Describe your album artwork or provide a link to the image (Dropbox, Google Drive, etc.)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">{formData.artworkDescription.length}/500 characters</p>
                </div>
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Short Bio <span className="text-orange-600">*</span>
                </label>
                <textarea
                  id="bio"
                  name="bio"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900 resize-none"
                  placeholder="Tell us about your music, your story, and what makes you different (max 500 characters)"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.bio.length}/500 characters</p>
              </div>

              {/* Voice Memo Section */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                  Station ID Voice Memo
                </label>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="mb-4">
                    <p className="text-gray-700 text-sm mb-2">Record yourself saying:</p>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <p className="text-gray-900 font-medium italic text-center">
                        "Hi, this is <span className="text-orange-600">{formData.artistName || '[Your Name]'}</span> and you're listening to my latest single (name) on RanchFire Radio. Your Home for Rooted. Real. Country."
                      </p>
                    </div>
                  </div>

                  {recordingError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        {recordingError}
                      </p>
                    </div>
                  )}

                  {!audioUrl ? (
                    <div className="flex flex-col items-center">
                      {isRecording ? (
                        <div className="text-center w-full">
                          <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-4 animate-pulse mx-auto">
                            <i className="ri-mic-fill text-white text-3xl"></i>
                          </div>
                          
                          {/* Audio Level Waveform */}
                          <div className="mb-4 bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center justify-center gap-1 h-20">
                              {[...Array(40)].map((_, i) => {
                                const barHeight = Math.max(
                                  10,
                                  Math.min(
                                    80,
                                    audioLevel * (0.5 + Math.random() * 0.5) * (1 - Math.abs(i - 20) / 30)
                                  )
                                );
                                return (
                                  <div
                                    key={i}
                                    className="w-1 bg-gradient-to-t from-orange-600 to-orange-400 rounded-full transition-all duration-75"
                                    style={{
                                      height: `${barHeight}%`,
                                    }}
                                  ></div>
                                );
                              })}
                            </div>
                            <div className="mt-2 flex items-center justify-center gap-2">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                              <span className="text-white text-xs font-bold uppercase tracking-wide">Recording</span>
                            </div>
                          </div>
                          
                          <p className="text-2xl font-bold text-gray-900 mb-2">{formatTime(recordingTime)}</p>
                          <p className="text-sm text-gray-500 mb-4">Recording... (max 30 seconds)</p>
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-stop-fill mr-2"></i>
                            Stop Recording
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="ri-mic-line text-orange-600 text-3xl"></i>
                          </div>
                          <p className="text-sm text-gray-500 mb-4">Click to start recording your station ID</p>
                          <button
                            type="button"
                            onClick={startRecording}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-mic-fill mr-2"></i>
                            Start Recording
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-green-600 text-2xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 text-sm">Voice Memo Recorded</p>
                          <p className="text-gray-500 text-xs">Duration: {formatTime(recordingTime)}</p>
                        </div>
                      </div>
                      
                      <audio src={audioUrl} controls className="w-full" />
                      
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={deleteRecording}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line mr-2"></i>
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            deleteRecording();
                            startRecording();
                          }}
                          className="flex-1 bg-orange-100 hover:bg-orange-200 text-orange-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-refresh-line mr-2"></i>
                          Re-record
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-bold flex items-center gap-2">
                  <i className="ri-check-line text-xl"></i>
                  Submission received! We'll review your music and be in touch soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-bold flex items-center gap-2">
                  <i className="ri-error-warning-line text-xl"></i>
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-600/30 whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Music'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
